import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BannerItem } from '@/utils/content';

interface HeroCarouselProps {
  banners: BannerItem[];
}

export default function HeroCarousel({ banners }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  if (banners.length === 0) return null;

  const goPrev = () => setIndex((prev) => (prev - 1 + banners.length) % banners.length);
  const goNext = () => setIndex((prev) => (prev + 1) % banners.length);

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden bg-primary-950" aria-label="焦点内容">
      {banners.map((banner, i) => (
        <div
          key={banner.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={banner.image}
            alt={`${banner.title}中文字幕在线观看`}
            title={`${banner.title} - 中文字幕在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 mb-4">
                  <i className="ri-fire-line"></i>
                  热门推荐
                </span>
                <h2 className="text-2xl md:text-5xl font-heading font-bold text-background-50">{banner.title}</h2>
                <p className="mt-2 text-sm md:text-base text-accent-200">{banner.subtitle}</p>
                <p className="mt-3 md:mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-xl">
                  {banner.description}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${banner.contentId}`}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    立即观看
                  </Link>
                  <Link
                    to="/category/movie"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-apps-2-line"></i>
                    浏览全部
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一个"
        onClick={goPrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 text-background-50 backdrop-blur flex items-center justify-center hover:bg-background-50/40 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一个"
        onClick={goNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 text-background-50 backdrop-blur flex items-center justify-center hover:bg-background-50/40 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {banners.map((banner, i) => (
          <button
            key={banner.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50'}`}
          ></button>
        ))}
      </div>
    </section>
  );
}